import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const soilsModule: ChecklistModuleDefinition = {
  code: 'soils',
  name: 'Soils & Land Use',
  description: 'Soil analysis, topography, and land use suitability',
  categories: [
    {
      code: 'soil_analysis',
      name: 'Soil Analysis',
      items: [
        {
          code: 'soil_survey_review',
          title: 'NRCS soil survey review',
          description: 'Review Web Soil Survey data for property',
          isGroupTask: false,
          dataFields: [
            { key: 'soil_types', label: 'Soil Types Present', type: 'array' },
            { key: 'dominant_soil', label: 'Dominant Soil Type', type: 'string' },
            { key: 'survey_source', label: 'Survey Source', type: 'string' },
          ],
        },
        {
          code: 'soil_capability_class',
          title: 'Soil capability classification',
          description: 'Review USDA capability class (I-VIII)',
          isGroupTask: false,
          dataFields: [
            { key: 'capability_classes', label: 'Capability Classes', type: 'array' },
            { key: 'prime_farmland_acres', label: 'Prime Farmland Acres', type: 'number', unit: 'acres' },
            { key: 'class_1_2_acres', label: 'Class I-II Acres', type: 'number', unit: 'acres' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Majority Class I-II soils' },
            { flag: 'green', condition: 'Good mix of productive soils' },
            { flag: 'yellow', condition: 'Limited productive soils' },
          ],
        },
        {
          code: 'prime_farmland',
          title: 'Prime farmland designation',
          description: 'Check for prime or unique farmland status',
          isGroupTask: false,
          dataFields: [
            { key: 'has_prime_farmland', label: 'Has Prime Farmland', type: 'boolean' },
            { key: 'prime_farmland_percent', label: 'Prime Farmland %', type: 'number', unit: 'percent' },
            { key: 'farmland_classifications', label: 'Classifications', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Significant prime farmland' },
          ],
        },
        {
          code: 'drainage_class',
          title: 'Drainage characteristics',
          description: 'Natural drainage class of soils',
          isGroupTask: false,
          dataFields: [
            { key: 'drainage_class', label: 'Drainage Class', type: 'select', options: [
              { value: 'excessively_drained', label: 'Excessively Drained' },
              { value: 'well_drained', label: 'Well Drained' },
              { value: 'moderately_well', label: 'Moderately Well Drained' },
              { value: 'somewhat_poorly', label: 'Somewhat Poorly Drained' },
              { value: 'poorly_drained', label: 'Poorly Drained' },
              { value: 'very_poorly', label: 'Very Poorly Drained' },
            ]},
            { key: 'drainage_improvements_needed', label: 'Improvements Needed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Well drained soils' },
            { flag: 'yellow', condition: 'Drainage concerns present' },
            { flag: 'red', condition: 'Poorly drained, extensive work needed' },
          ],
        },
        {
          code: 'percolation_rate',
          title: 'Percolation assessment',
          description: 'Soil percolation for septic suitability',
          isGroupTask: false,
          dataFields: [
            { key: 'perc_suitable', label: 'Perc Suitable', type: 'boolean' },
            { key: 'perc_rate', label: 'Perc Rate', type: 'string' },
            { key: 'septic_limitations', label: 'Septic Limitations', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Soils not suitable for septic' },
            { flag: 'yellow', condition: 'May require engineered system' },
            { flag: 'green', condition: 'Good percolation' },
          ],
        },
        {
          code: 'soil_testing',
          title: 'On-site soil testing',
          description: 'Physical soil samples and testing if needed',
          isGroupTask: false,
          dataFields: [
            { key: 'testing_done', label: 'Testing Completed', type: 'boolean' },
            { key: 'test_date', label: 'Test Date', type: 'date' },
            { key: 'ph_level', label: 'pH Level', type: 'number' },
            { key: 'organic_matter', label: 'Organic Matter %', type: 'number', unit: 'percent' },
            { key: 'nutrients', label: 'Nutrient Results', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'topography',
      name: 'Topography',
      items: [
        {
          code: 'slope_analysis',
          title: 'Slope analysis',
          description: 'Review terrain slope and grade',
          isGroupTask: false,
          dataFields: [
            { key: 'average_slope', label: 'Average Slope', type: 'number', unit: 'percent' },
            { key: 'max_slope', label: 'Max Slope', type: 'number', unit: 'percent' },
            { key: 'slope_distribution', label: 'Distribution', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Gentle to moderate slopes' },
            { flag: 'yellow', condition: 'Steep areas present' },
            { flag: 'red', condition: 'Severe slopes limiting use' },
          ],
        },
        {
          code: 'elevation_range',
          title: 'Elevation assessment',
          description: 'Property elevation range and high/low points',
          isGroupTask: false,
          dataFields: [
            { key: 'min_elevation', label: 'Min Elevation', type: 'number', unit: 'feet' },
            { key: 'max_elevation', label: 'Max Elevation', type: 'number', unit: 'feet' },
            { key: 'total_relief', label: 'Total Relief', type: 'number', unit: 'feet' },
          ],
        },
        {
          code: 'usable_land',
          title: 'Usable land assessment',
          description: 'Estimate of practically usable acreage',
          isGroupTask: false,
          dataFields: [
            { key: 'total_acres', label: 'Total Acres', type: 'number', unit: 'acres' },
            { key: 'usable_acres', label: 'Usable Acres', type: 'number', unit: 'acres' },
            { key: 'usable_percent', label: 'Usable %', type: 'number', unit: 'percent' },
            { key: 'limiting_factors', label: 'Limiting Factors', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: '>80% usable' },
            { flag: 'green', condition: '60-80% usable' },
            { flag: 'yellow', condition: '40-60% usable' },
            { flag: 'red', condition: '<40% usable' },
          ],
        },
        {
          code: 'buildable_sites',
          title: 'Building site evaluation',
          description: 'Identify suitable building locations',
          isGroupTask: false,
          dataFields: [
            { key: 'sites_identified', label: 'Sites Identified', type: 'number' },
            { key: 'best_site_notes', label: 'Best Site Notes', type: 'string' },
            { key: 'grading_needed', label: 'Grading Needed', type: 'select', options: [
              { value: 'none', label: 'None/Minimal' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'significant', label: 'Significant' },
            ]},
          ],
          suggestedFlags: [
            { flag: 'green', condition: 'Good building sites available' },
            { flag: 'yellow', condition: 'Limited/challenging sites' },
            { flag: 'red', condition: 'No suitable building sites' },
          ],
        },
        {
          code: 'erosion_risk',
          title: 'Erosion assessment',
          description: 'Evaluate erosion risk and existing erosion',
          isGroupTask: false,
          dataFields: [
            { key: 'erosion_present', label: 'Erosion Present', type: 'boolean' },
            { key: 'erosion_severity', label: 'Severity', type: 'select', options: [
              { value: 'none', label: 'None' },
              { value: 'minor', label: 'Minor' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'severe', label: 'Severe' },
            ]},
            { key: 'erosion_types', label: 'Types', type: 'array' },
            { key: 'remediation_needed', label: 'Remediation Needed', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Severe erosion issues' },
            { flag: 'yellow', condition: 'Moderate erosion requiring attention' },
          ],
        },
      ],
    },
    {
      code: 'land_use_suitability',
      name: 'Land Use Suitability',
      items: [
        {
          code: 'crop_suitability',
          title: 'Crop suitability ratings',
          description: 'NRCS ratings for various crops',
          isGroupTask: false,
          dataFields: [
            { key: 'suitable_crops', label: 'Suitable Crops', type: 'array' },
            { key: 'target_crop', label: 'Target Crop', type: 'string' },
            { key: 'target_suitability', label: 'Target Suitability', type: 'select', options: [
              { value: 'well_suited', label: 'Well Suited' },
              { value: 'moderately_suited', label: 'Moderately Suited' },
              { value: 'poorly_suited', label: 'Poorly Suited' },
              { value: 'not_suited', label: 'Not Suited' },
            ]},
          ],
        },
        {
          code: 'pasture_suitability',
          title: 'Pasture/grazing suitability',
          description: 'Assessment for livestock grazing',
          isGroupTask: false,
          dataFields: [
            { key: 'pasture_rating', label: 'Pasture Rating', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'carrying_capacity', label: 'Est. Carrying Capacity', type: 'string' },
            { key: 'improvements_needed', label: 'Improvements Needed', type: 'array' },
          ],
        },
        {
          code: 'orchard_suitability',
          title: 'Orchard/vineyard suitability',
          description: 'Suitability for tree crops',
          isGroupTask: false,
          dataFields: [
            { key: 'suitability', label: 'Suitability', type: 'select', options: [
              { value: 'well_suited', label: 'Well Suited' },
              { value: 'moderately_suited', label: 'Moderately Suited' },
              { value: 'poorly_suited', label: 'Poorly Suited' },
            ]},
            { key: 'limiting_factors', label: 'Limiting Factors', type: 'array' },
          ],
        },
        {
          code: 'woodland_suitability',
          title: 'Woodland suitability',
          description: 'Suitability for timber/forestry',
          isGroupTask: false,
          dataFields: [
            { key: 'site_index', label: 'Site Index', type: 'number' },
            { key: 'suitable_species', label: 'Suitable Species', type: 'array' },
            { key: 'timber_rating', label: 'Timber Rating', type: 'string' },
          ],
        },
      ],
    },
  ],
};

